import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock-upgrade";
import { LineItem, RecommendedCartProduct } from "../components";
import { formatPrice } from "../helpers";
import PanelController from "./panel-controller";

export default class extends PanelController {
  static targets = [
    "lineItems",
    "subtotal",
    "originalTotal",
    "basket",
    "recommendedProduct",
    "recommendation",
    "footer",
    "loader",
  ];
  static values = { product: String };

  initialize() {
    if (this.hasLineItemsTarget) {
      const lineItem = this.lineItemsTarget.querySelector(".line-item");

      if (lineItem && this.hasRecommendedProductTarget) {
        this.handleRecommendedProducts(this.productValue);
      }
    }
  }

  async addToCart(items = []) {
    try {
      const res = await fetch("/cart/add.js", {
        method: "post",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          items,
        }),
      });

      if (typeof window.stackupRefreshFreeShippingBar === 'function') {
        window.stackupRefreshFreeShippingBar();
      }

      if (res.ok) {
        return res;
      } else {
        console.log("There was an error adding to the cart: ", res);
        return res;
      }
    } catch (err) {
      console.log("There was an error adding to the cart: ", err);
    }
  }

  async getCartData() {
    try {
      const res = await fetch("/cart.js", {
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (res.ok) {
        const data = await res.json();

        return data;
      } else {
        console.error("There was an error fetching bag data:", res);
      }
    } catch (error) {
      console.error("There was an error fetching bag data:", error);
    }
  }

  buildCart(items, elem, cartData) {
    const cartHtml =
      items
        .map((item) => {
          const items = JSON.parse(this.panelTarget.dataset.items);
          const variant_inventory =
            items?.find((_item) => _item.variant_id === item.variant_id)
              ?.inventory_quantity ||
            elem?.querySelector('[name="quantity"]').max ||
            null;

          return LineItem({ ...item, variant_inventory });
        })
        ?.join("") || "";

    this.lineItemsTarget.innerHTML = cartHtml;

    if (cartData) {
      this.handleBasketTotal(cartData);
      this.updateSubtotal(cartData);
    }

    if (this.hasFooterTarget) {
      if (items.length > 0) {
        this.basketTarget.style.opacity = 1;
        this.footerTarget.classList.remove("hidden");

        if (this.hasRecommendedProductTarget) {
          this.recommendationTarget.classList.remove("hidden");
          this.handleRecommendedProducts(items[0].product_id || null);
        }
      } else {
        this.footerTarget.classList.add("hidden");
      }
    }
  }

  async handleRecommendedProducts(id) {
    this.recommendedProductTarget.innerHTML = "Finding Recommendations...";
    if (id) {
      try {
        const res = await fetch(
          `/recommendations/products.json?product_id=${id}&limit=10`
        );

        if (res.ok) {
          const data = await res.json();

          if (data.products) {
            const filteredProducts = data.products.filter((product) => {
              const lineItems = Array.from(
                this.lineItemsTarget.querySelectorAll(".line-item")
              );

              return !lineItems?.some((item) => {
                return item.dataset.product == product.id;
              });
            });

            if (filteredProducts.length > 0) {
              const product = filteredProducts.find(
                (product) => product.available
              );

              try {
                const res = await fetch(
                  `/products/${product.handle}?view=json`
                );

                if (res.ok) {
                  const productData = await res.json();

                  return (this.recommendedProductTarget.innerHTML =
                    RecommendedCartProduct(productData));
                } else {
                  console.log("There was an error fetching...", res);
                }
              } catch (error) {
                console.log("Error fetching product data...", error);
              }
            } else {
              if (window.location.pathname === "/cart") {
                this.element
                  .querySelector(".cart__recommendation")
                  .classList.add("hidden");
              } else {
                this.element
                  .querySelector(".panel-recommendations")
                  .classList.add("hidden");
              }
            }
          }
        } else {
          console.error("There was an error", res);
        }
      } catch (error) {
        console.error("There was an error", error);
      }
    }
  }

  async handleCart(elem = null, toggle = true) {
    if (document.querySelector(".section-cart__empty")) {
      const cart = document.querySelector("section.cart");

      cart.classList.add("section-cart--transparent");
      cart.classList.add("cart--empty");

      if (this.hasLoaderTarget) {
        this.loaderTarget.classList.remove("hidden");
      }

      fetch(`/cart?section_id=${cart.id}`)
        .then((res) => res.text())
        .then((text) => {
          const html = document.createElement("div");
          html.innerHTML = text;
          cart.innerHTML = html.innerHTML;
          if (this.hasLoaderTarget) {
            this.loaderTarget.classList.add("hidden");
          }
          cart.classList.remove("section-cart--transparent");

          const firstLine = document.querySelector(".cart__items .line-item");
          firstLine &&
            this.handleRecommendedProducts(firstLine.dataset.product);
        });
    } else {
      const cartData = await this.getCartData();

      if (cartData) {
        const { items } = cartData;
        if (items?.length > 0) {
          this.buildCart(items, elem, cartData);
        }

        if (this.panelTarget.querySelector(".panel__body")) {
          this.panelTarget
            .querySelector(".panel__body")
            .classList.add("panel__body--spaced");
        }
        toggle && this.togglePanel();

        if (this.hasLineItemsTarget && window.location.pathname !== "/cart") {
          disableBodyScroll(this.lineItemsTarget);
        }
      }
    }
  }

  // Header cart icon trigger: toggle drawer, fetching fresh cart data when opening.
  toggleCart(e) {
    e.preventDefault();

    if (this.hasPanelTarget && this.panelTarget.classList.contains("panel--active")) {
      this.togglePanel(e);
      return;
    }

    this.handleCart(null, true);
  }

  async handleEmptyCart() {
    this.lineItemsTarget.innerHTML = "<p>Your bag is empty</p>";
    this.hasFooterTarget && this.footerTarget.classList.add("hidden");

    if (this.hasPanelTarget && this.panelTarget.querySelector(".panel__body")) {
      this.panelTarget
        .querySelector(".panel__body")
        .classList.remove("panel__body--spaced");
    }

    if (this.hasRecommendedProductTarget) {
      this.recommendationTarget.classList.add("hidden");
    }

    const cart = document.querySelector(".template-cart section.cart");
    if (document.querySelector(".template-cart")) {
      if (this.hasLoaderTarget) {
        this.loaderTarget.classList.remove("hidden");
      }
      cart.classList.add("section-cart--transparent");
      fetch(`/cart?section_id=${cart.id}`)
        .then((res) => res.text())
        .then((text) => {
          const html = document.createElement("div");
          html.innerHTML = text;
          cart.innerHTML = html.innerHTML;
          if (this.hasLoaderTarget) {
            this.loaderTarget.classList.add("hidden");
          }
          cart.classList.remove("section-cart--transparent");
        });
      if (
        window.location.pathname === "/cart" &&
        this.element.querySelector("form.form--cart")
      ) {
        this.element.querySelector("form.form--cart").innerHTML = `
        <div class="section-cart__empty">
          <h2>
            Bag,
          </h2>

          <h6>
          Your bag is Empty
          </h6>

          <p>
          Discover magical accessories for you and your home
          </p>

          <div class="section-cart__empty-actions">
            <a class="btn btn--primary btn--pink btn--block" href="/collections/home-furnishings">Continue Shopping</a>
          </div>
        </div>
      `;
      }
    }
  }

  handleBasketTotal(data) {
    if (data) {
      this.basketTargets.forEach(
        // (target) => (target.querySelector("span").innerText = data.item_count)
        (target) => (target.innerText = data.item_count)
      );
    }
  }

  updateSubtotal(data) {
    if (data) {
      this.subtotalTargets.forEach(
        (target) =>
          (target.innerText = formatPrice(data.total_price, true, false, false))
      );

      this.originalTotalTargets.forEach((target) => {
        target.innerText = formatPrice(
          data.original_total_price,
          true,
          false,
          false
        );

        if (data.total_price < data.original_total_price) {
          target.classList.remove("hidden");
        } else {
          target.classList.add("hidden");
        }
      });

      if (typeof window.stackupRefreshFreeShippingBar === 'function') {
        window.stackupRefreshFreeShippingBar();
      }
    }
  }

  async addRecommendedProduct(e) {
    const variantId = e.params.variant;
    const cc_sku = e.params.sku;

    await this.addToCart([
      {
        id: variantId,
        quantity: 1,
        properties: { _cc_sku: cc_sku, is_sample: false },
      },
    ]);

    const items = this.panelTarget.dataset.items;
    this.handleCart(null, false);

    enableBodyScroll(this.panelTarget);

    if (window.location.pathname === "/cart") {
      this.handleRecommendedProducts(this.productValue);
      window.location.reload();
    }
  }

  get lineItemController() {
    return this.application.controllers.find((controller) => {
      return controller.context.identifier === "line-item";
    });
  }
}
